import React, { useState, useEffect, useRef, Suspense } from "react"
import { useSelector, useDispatch } from "react-redux"
import {
  fetchProducts,
  updateProduct,
  deleteProduct,
  createProduct,
} from "../../store"
import ReactPaginate from "react-paginate"
import { ArrowBigLeftDashIcon, ArrowBigRightDashIcon } from "lucide-react"
import { Modal, ModalHeader, ModalActions } from "../ui/Modal"
import ProductDetails from "../ui/ProductDetails"
import AdminProductChart from "../ui/AdminProductChart"
import Spinner from "../Spinner"
import { useToast } from "../../hooks/useToast"
import "../styles/AdminProductPage.css"

function AdminProductPage() {
  const { products } = useSelector((state) => state.products)
  const [selectedProduct, setSelectedProduct] = useState(null)
  const [selectedCategory, setSelectedCategory] = useState(null)
  const [selectedPriceFilter, setSelectedPriceFilter] = useState(null)
  const [itemsPerPage, setItemsPerPage] = useState(24)
  const [itemOffset, setItemOffset] = useState(0)
  const [pendingChanges, setPendingChanges] = useState(false)
  const [isAdding, setIsAdding] = useState(false)
  const { notify } = useToast()

  const endOffset = itemOffset + itemsPerPage

  //TODO: add pagination
  //TODO: add search
  //TODO: add filter

  const dispatch = useDispatch()
  useEffect(() => {
    dispatch(fetchProducts())
  }, [])

  const handlePageClick = (event) => {
    const newOffset = (event.selected * itemsPerPage) % products.length
    setItemOffset(newOffset)
    window.scrollTo(0, 0)
  }

  const priceFilter = (product) => {
    if (selectedPriceFilter === "<$100") {
      return product.price < 100
    } else if (selectedPriceFilter === "<$1000") {
      return product.price < 1000
    } else {
      return true
    }
  }

  //get categories list from products, then filter out duplicates
  const filterProducts = selectedPriceFilter
    ? products.filter(priceFilter)
    : selectedCategory
    ? products.filter((product) => product.category === selectedCategory)
    : products.length > 0
    ? products
    : []

  const categories =
    products.length > 0 ? products.map((product) => product.category) : []
  const uniqueCategories = [...new Set(categories)]

  const currentProducts =
    filterProducts.length > 0 ? filterProducts.slice(itemOffset, endOffset) : []
  const pageCount = Math.ceil(filterProducts.length / itemsPerPage)

  const handleCategoryChange = (event) => {
    if (event.target.value === "All") setSelectedCategory(null)
    else {
      setSelectedCategory(event.target.value)
    }
  }

  const handlePriceChange = (event) => {
    if (event.target.value === "All") setSelectedPriceFilter(null)
    else {
      setSelectedPriceFilter(event.target.value)
    }
  }

  const handleDeleteClick = (productId) => {
    if (window.confirm("Are you sure you want to --DELETE-- this product?")) {
      dispatch(deleteProduct(productId))
        .then(() => {
          console.log("Product Deleted Successfully")
          notify("Product Deleted Successfully!", "success")
          dispatch(fetchProducts())
        })
        .catch((error) => {
          console.error("Error Deleting Product:", error)
          notify("Error Seleting Product.", "error")
        })
    }
  }

  const handleDetailsClick = (product) => {
    setSelectedProduct(product)
  }

  const onFileChange = (event) => {
    const reader = new FileReader()
    reader.readAsDataURL(event.target.files[0])
    reader.onload = () => {
      setSelectedProduct({ ...selectedProduct, imageURL: reader.result })
    }
    reader.onerror = function (error) {
      console.log("Error: ", error)
    }
  }
  const handleAddProductClick = () => {
    setIsAdding(true)
    setSelectedProduct({
      name: "",
      category: "Category1",
      price: 0.0,
      material: "",
      quantity: 0,
      description: "",
      imageURL: "",
    })
  }

  const handleInputChange = (event) => {
    const { name, value } = event.target
    setSelectedProduct({ ...selectedProduct, [name]: value })
    if (isAdding) {
      if (
        selectedProduct.name !== "" &&
        selectedProduct.category !== "" &&
        selectedProduct.price !== 0 &&
        selectedProduct.material !== "" &&
        selectedProduct.quantity !== 0 &&
        selectedProduct.description !== "" &&
        selectedProduct.imageURL !== ""
      ) {
        setPendingChanges(true)
      }
    } else {
      setPendingChanges(true)
    }
  }

  const handleModalClose = () => {
    setSelectedProduct(null)
    setIsAdding(false)
    setPendingChanges(false)
  }

  const handleSubmit = () => {
    dispatch(updateProduct(selectedProduct))
      .then(() => {
        notify("Product Updated Successfully!", "success")
      })
      .catch((error) => {
        console.error("Error Updating Product:", error)
        notify("Error Updating Product.", "error")
      })
      .finally(() => {
        setPendingChanges(false)
        setSelectedProduct(null)
        setIsAdding(false)
      })
  }

  const handleAdd = () => {
    dispatch(createProduct(selectedProduct))
      .then(() => {
        notify("Product Added Successfully!", "success")
      })
      .catch((error) => {
        console.error("Error Adding Product:", error)
        notify("Error Adding Product.", "error")
      })
      .finally(() => {
        setPendingChanges(false)
        setSelectedProduct(null)
        setIsAdding(false)
      })
  }

  return (
    <div className="adminProductPageContainer">
      <div className="adminProductSearch">
        {selectedProduct && (
          <Modal
            open={selectedProduct}
            className="modal"
            responsive
            onClickBackdrop={() => handleModalClose()}
          >
            <ModalHeader className="modal__header">
              {selectedProduct && selectedProduct.name}
            </ModalHeader>
            <div className="modal__body card">
              <ProductDetails product={selectedProduct} />
              <div className="modal__container"></div>
              <div className="modal__container2">
                <div className="modal_form">
                  <label>
                    <span className="label-text">Product Name</span>
                  </label>
                  <input
                    type="text"
                    placeholder="Product Name"
                    name="name"
                    className="input input-bordered"
                    value={selectedProduct.name}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
              <div className="modal__container2">
                <div className="modal_form">
                  <label>
                    <span className="label-text">Category</span>
                  </label>
                  <select
                    placeholder="Category"
                    name="category"
                    className="select select-bordered"
                    value={selectedProduct.category}
                    onChange={handleInputChange}
                  >
                    {uniqueCategories.map((category) => {
                      return <option key={category}>{category}</option>
                    })}
                  </select>
                </div>
                <div className="modal_form">
                  <label>
                    <span className="label-text">Price</span>
                  </label>
                  <input
                    type="number"
                    placeholder="Price"
                    name="price"
                    min={0}
                    step="0.01"
                    className="input input-bordered"
                    value={selectedProduct.price}
                    onChange={handleInputChange}
                    onFocus={() => {
                      if (selectedProduct.price === 0) {
                        setSelectedProduct({ ...selectedProduct, price: "" })
                      }
                    }}
                  />
                </div>
              </div>
              <div className="modal__container2">
                <div className="modal_form">
                  <label>
                    <span className="label-text">Product Material</span>
                  </label>
                  <input
                    type="text"
                    placeholder="Product Material"
                    name="material"
                    className="input input-bordered"
                    value={selectedProduct.material}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="modal_form">
                  <label>
                    <span className="label-text">Quantity Available</span>
                  </label>
                  <input
                    type="number"
                    placeholder="Quantity"
                    name="quantity"
                    min={0}
                    step={1}
                    className="input input-bordered"
                    value={selectedProduct.quantity}
                    onChange={handleInputChange}
                    onFocus={() => {
                      if (selectedProduct.quantity === 0) {
                        setSelectedProduct({ ...selectedProduct, quantity: "" })
                      }
                    }}
                  />
                </div>
              </div>
              <div className="modal__container2">
                <div className="modal_form">
                  <label>
                    <span className="label-text">Product Image</span>
                  </label>
                  <input
                    type="file"
                    className="file-input file-input-bordered w-3/4"
                    onChange={onFileChange}
                  />
                </div>
              </div>
              <div className="modal__container2">
                <div className="modal_form">
                  <label>
                    <span className="label-text">Description</span>
                  </label>
                  <textarea
                    placeholder="Description"
                    name="description"
                    className="textarea-product input-bordered"
                    value={selectedProduct.description}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
              <ModalActions>
                <button
                  className="save-button-primary"
                  disabled={!pendingChanges}
                  onClick={() => (isAdding ? handleAdd() : handleSubmit())}
                >
                  {isAdding ? "Create" : "Save"}
                </button>
              </ModalActions>
            </div>
          </Modal>
        )}
      </div>
      <AdminProductChart />
      <div className="padding-md">
        <h1 className="admin-table-heading">Products</h1>
        <div className="cat-container">
          <div className="modal_form">
            <label>
              <span className="label-text">Category</span>
            </label>
            <select
              className="category-change"
              defaultValue={"All"}
              onChange={handleCategoryChange}
            >
              <option>All</option>
              {uniqueCategories.map((category) => {
                return <option key={category}>{category}</option>
              })}
            </select>
          </div>
          <div className="modal_form">
            <label>
              <span className="label-text">Price</span>
            </label>
            <select
              className="category-change"
              defaultValue="All"
              onChange={handlePriceChange}
            >
              <option>All</option>
              <option>{"<$100"}</option>
              <option>{"<$1000"}</option>
            </select>
          </div>
          <div className="modal_form">
            <label>
              <span className="label-text">Actions</span>
            </label>
            <button
              className="addProductButton"
              onClick={() => handleAddProductClick()}
            >
              Add Product
            </button>
          </div>
        </div>

        <div className="adminProductTableWrapper">
          <table className="adminProductTable">
            <thead className="adminProductTableHeader">
              <tr>
                <th>Product</th>
                <th>Description | Category</th>
                <th>Price</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {currentProducts.length > 0 &&
                currentProducts.map((product) => {
                  return (
                    <tr
                      key={product.id}
                      className={`${
                        selectedProduct &&
                        selectedProduct.id === product.id &&
                        "table__row"
                      }`}
                    >
                      <td>
                        <div className="spinner-avatar">
                          <Suspense fallback={<Spinner />}>
                            <div className="avatar">
                              <div className="mask">
                                <img
                                  src={product.imageURL}
                                  alt={product.name}
                                  loading="lazy"
                                />
                              </div>
                            </div>
                          </Suspense>
                          <div>
                            <div className="product__name">{product.name}</div>
                            <div className="product__material">
                              {product.material}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className="text-ellipsis">
                          {product.description}
                        </span>
                        <br />
                        <span className="product__category">
                          {product.category}
                        </span>
                      </td>
                      <td className="table__cell">${product.price}</td>
                      <td>
                        <div className="admin-orders-action-cell">
                          <button
                            className="admin-orders-table-action-button"
                            onClick={() => handleDetailsClick(product)}
                          >
                            Details
                          </button>
                          <button
                            className="delete-button"
                            onClick={() => handleDeleteClick(product.id)}
                          >
                            <svg
                              className="delete-svgIcon"
                              viewBox="0 0 448 512"
                            >
                              <path d="M135.2 17.7L128 32H32C14.3 32 0 46.3 0 64S14.3 96 32 96H416c17.7 0 32-14.3 32-32s-14.3-32-32-32H320l-7.2-14.3C307.4 6.8 296.3 0 284.2 0H163.8c-12.1 0-23.2 6.8-28.6 17.7zM416 128H32L53.2 467c1.6 25.3 22.6 45 47.9 45H346.9c25.3 0 46.3-19.7 47.9-45L416 128z"></path>
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  )
                })}
            </tbody>
          </table>
          {/* Apply your pagination styles */}
          <ReactPaginate
            breakLabel="..."
            nextLabel={<ArrowBigRightDashIcon size={24} alt="Next" />}
            onPageChange={handlePageClick}
            pageRangeDisplayed={5}
            pageCount={pageCount}
            previousLabel={<ArrowBigLeftDashIcon size={24} alt="Previous" />}
            renderOnZeroPageCount={null}
            className="adminProductPagination"
            activeClassName="adminProductPaginationActive"
            pageClassName="adminProductPaginationPage"
            previousClassName="join-item table__cell bg-base-300 hover:bg-base-200 flex items-center"
            nextClassName="join-item table__cell bg-base-300 hover-bg-base-200 flex items-center"
          />
        </div>
      </div>
    </div>
  )
}

export default AdminProductPage
